import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Signup() {
  const { t } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success(t("welcome"));
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <h1 className="font-display text-3xl sm:text-4xl font-black text-[#1A1A1A] text-center">{t("register")}</h1>

        <div className="mt-8 jh-card p-7">
          <Button
            onClick={handleGoogle}
            type="button"
            variant="outline"
            className="w-full rounded-full py-6 border-2 border-[#EAEAEA] hover:border-[#E05D3A]"
            data-testid="google-signup-btn"
          >
            {t("continueWithGoogle")}
          </Button>
          <div className="flex items-center gap-3 my-6 text-sm text-[#6B6B6B]">
            <div className="flex-1 h-px bg-[#EAEAEA]" /> {t("or")} <div className="flex-1 h-px bg-[#EAEAEA]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t("name")}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="mt-1.5"
                data-testid="signup-name"
              />
            </div>
            <div>
              <Label>{t("email")}</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-1.5"
                data-testid="signup-email"
              />
            </div>
            <div>
              <Label>{t("password")}</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                  className="pe-11"
                  autoComplete="new-password"
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 end-3 flex items-center text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors"
                  aria-label={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
                  data-testid="toggle-signup-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-6"
              data-testid="signup-submit-btn"
            >
              {loading ? t("processing") : t("register")}
            </Button>
          </form>

          <p className="text-center text-sm text-[#6B6B6B] mt-6">
            {t("alreadyHaveAccount")}{" "}
            <Link to="/login" className="text-[#E05D3A] font-semibold hover:underline">{t("login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
